import { inject, Injectable } from "@angular/core";
import { SegmentService } from "@main-services/segment.service";
import {
	CancellationStatus,
	CancelResponses,
	OrderFlow
} from "@modules/limit-orders/interfaces/limit-order-rudderstack.model";
import { OrderStatus, OrderType } from "@gql/offer";
import { BehaviorSubject } from "rxjs";
import { ILimitOrderDetailVM } from "@modules/limit-orders/components/limit-order-detail/limit-order-detail.facade";
import { getOrderStatus } from "@modules/limit-orders/utils/limit-orders-util";
import {
	OrderTypeTrackingProperty,
	TradeTrackingName,
	TradeTypeTrackingProperty
} from "@modules/checkout/entities/trade.rudderstack.tracking.model";
import { RudderstackDynamicProperties, RudderstackService } from "@main-services/rudderstack.service";

@Injectable({
	providedIn: "root"
})
export class LimitOrdersTrackingFacade {
	private readonly segmentService = inject(SegmentService);
	private readonly rudderstackService = inject(RudderstackService);

	private flowStarted$$: BehaviorSubject<OrderFlow> = new BehaviorSubject(null);
	public readonly flowStarted$ = this.flowStarted$$.asObservable();

	public trackRudderstack(model: TradeTrackingName, properties: Partial<RudderstackDynamicProperties>): void {
		this.segmentService.track({
			name: model,
			properties
		});
		this.rudderstackService.track({
			name: model,
			properties
		});
	}

	public rudderstackTracker(
		model: TradeTrackingName,
		orderType: OrderType,
		cancellationStatus?: string,
		isSlippage?: boolean
	): void {
		const trade_type = orderType === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = OrderTypeTrackingProperty.limit_order;
		const cancelStatus =
			cancellationStatus === CancelResponses.Pending
				? CancellationStatus.Pending
				: cancellationStatus === CancelResponses.Success
					? CancellationStatus.Success
					: CancellationStatus.Error;

		const properties: RudderstackDynamicProperties = {
			order_type,
			trade_type,
			info_type: isSlippage ? "maximum_slippage" : "execution_price"
		};
		// Check if there is a cancellation status
		cancellationStatus !== undefined && (properties.cancellation_status = cancelStatus);

		this.segmentService.track({
			name: model,
			properties: properties
		});
		this.rudderstackService.track({
			name: model,
			properties: properties
		});
	}

	public rudderstackTrackerFlow(model: TradeTrackingName, orderType: OrderType, flow: OrderFlow): void {
		const trade_type = orderType === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = OrderTypeTrackingProperty.limit_order;

		this.segmentService.track({
			name: model,
			properties: {
				trade_type,
				order_type,
				flow: flow
			}
		});
		this.rudderstackService.track({
			name: model,
			properties: {
				trade_type,
				order_type,
				flow: flow
			}
		});
	}

	public rudderstackTrackerDestination(model: TradeTrackingName, orderType: OrderType, destination: string): void {
		const trade_type = orderType === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = OrderTypeTrackingProperty.limit_order;

		const properties: Partial<RudderstackDynamicProperties> = {
			trade_type,
			order_type,
			destination
		};

		this.segmentService.track({
			name: model,
			properties
		});
		this.rudderstackService.track({
			name: model,
			properties
		});
	}

	public rudderstackTrackerChipTapped(
		orderType: OrderType,
		percentage: number,
		isMarginLimitOrder: boolean = false
	): void {
		const trade_type = orderType === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = isMarginLimitOrder
			? OrderTypeTrackingProperty.margin_limit_order
			: OrderTypeTrackingProperty.limit_order;

		const properties: Partial<RudderstackDynamicProperties> = {
			trade_type,
			order_type,
			percentage
		};

		this.segmentService.track({
			name: TradeTrackingName.TradeAmountScreenChipsTapped,
			properties
		});
		this.rudderstackService.track({
			name: TradeTrackingName.TradeAmountScreenChipsTapped,
			properties
		});
	}

	public rudderstackTrackerAssetType(model: TradeTrackingName, orderType: OrderType, assetType: string): void {
		const trade_type = orderType === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = OrderTypeTrackingProperty.limit_order;

		this.segmentService.track({
			name: model,
			properties: {
				trade_type,
				order_type,
				asset_type: assetType
			}
		});
		this.rudderstackService.track({
			name: model,
			properties: {
				trade_type,
				order_type,
				asset_type: assetType
			}
		});
	}

	public rudderstackTrackerOrderDetails(model: TradeTrackingName, vm: Partial<ILimitOrderDetailVM>): void {
		const trade_type = vm.type === OrderType.Buy ? TradeTypeTrackingProperty.buy : TradeTypeTrackingProperty.sell;
		const order_type = OrderTypeTrackingProperty.limit_order;

		const isNewBuySeen =
			vm.status === OrderStatus.Cancelled || vm.status === OrderStatus.Failed || vm.status === OrderStatus.Done;
		const isFundWalletSeen =
			(vm.status === OrderStatus.Pending || vm.status === OrderStatus.InProgress) &&
			vm.maxBuy - vm.buyPower < vm.minBuy;

		const properties: RudderstackDynamicProperties = {
			trade_type,
			order_type,
			fund_wallet_button_viewed: isFundWalletSeen,
			new_order_button_viewed: isNewBuySeen,
			status: getOrderStatus(vm.status)
		};

		this.segmentService.track({
			name: model,
			properties: properties
		});
		this.rudderstackService.track({
			name: model,
			properties: properties
		});
	}

	//TODO: refactor to include the order type and trade type
	public widgetPillTapped(model: TradeTrackingName, pill: string): void {
		const properties: RudderstackDynamicProperties = {
			type: pill
		};
		this.segmentService.track({
			name: model,
			properties: properties
		});
		this.rudderstackService.track({
			name: model,
			properties: properties
		});
	}

	public updateFlow(flow: OrderFlow): void {
		this.flowStarted$$.next(flow);
	}
}
